import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, TrendingUp, Settings2 } from 'lucide-react';
import {
  useAbsenceTrends,
  usePresenceDistribution,
  useAbsenceAlertThreshold,
  useUpdateAbsenceAlertThreshold,
  useAnalyticsGroups,
  useAnalyticsChildren,
  useCheckAbsenceAlert
} from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';

const COLORS = {
  present: '#22c55e',
  plannedAbsence: '#f59e0b',
  unplannedAbsence: '#ef4444',
  threshold: '#dc2626'
};

export default function AbsenceSection({ branchId }) {
  const [period, setPeriod] = useState('weekly');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showThresholdSettings, setShowThresholdSettings] = useState(false);

  // Data hooks
  const { data: groups = [] } = useAnalyticsGroups(branchId);
  const { data: children = [] } = useAnalyticsChildren(branchId, selectedGroup);
  const { data: savedThreshold = 20 } = useAbsenceAlertThreshold(branchId);
  const updateThreshold = useUpdateAbsenceAlertThreshold();

  // Local state for responsive slider (synced with saved value)
  const [localThreshold, setLocalThreshold] = useState(savedThreshold);

  // Sync local state when saved value changes (e.g., on initial load)
  useEffect(() => {
    setLocalThreshold(savedThreshold);
  }, [savedThreshold]);

  const {
    data: trendsData,
    isLoading: trendsLoading
  } = useAbsenceTrends(branchId, period, selectedGroup);

  const {
    data: distributionData,
    isLoading: distributionLoading
  } = usePresenceDistribution(branchId, period, selectedGroup, selectedChild);

  // Absence alert notification
  const checkAbsenceAlert = useCheckAbsenceAlert();
  const alertCheckedRef = useRef(false);

  // Check today's absence and trigger notification if threshold exceeded
  useEffect(() => {
    if (!trendsData?.data || alertCheckedRef.current || selectedGroup) return;

    // Find today's data
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayData = trendsData.data.find(d => d.date === today);

    if (todayData && todayData.absentPercent > savedThreshold) {
      alertCheckedRef.current = true;
      checkAbsenceAlert.mutate({
        branchId,
        absentPercent: todayData.absentPercent,
        threshold: savedThreshold,
        date: today
      });
    }
  }, [trendsData, savedThreshold, branchId, selectedGroup]);

  // Check if any day exceeds saved threshold (for alert badge)
  const hasAlert = trendsData?.data?.some(day => day.absentPercent > savedThreshold);

  // Format date for chart
  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'd MMM', { locale: pl });
  };

  // Pie chart data
  const pieData = distributionData ? [
    { name: 'Obecni', value: distributionData.present, color: COLORS.present },
    { name: 'Planowana nieobecność', value: distributionData.plannedAbsence, color: COLORS.plannedAbsence },
    { name: 'Nieplanowana nieobecność', value: distributionData.unplannedAbsence, color: COLORS.unplannedAbsence }
  ].filter(item => item.value > 0) : [];

  // Handle slider drag (immediate visual feedback)
  const handleThresholdChange = (value) => {
    setLocalThreshold(value[0]);
  };

  // Handle slider release (save to database)
  const handleThresholdCommit = (value) => {
    updateThreshold.mutate({ branchId, threshold: value[0] });
  };

  return (
    <div className="space-y-6">
      {/* Section Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Nieobecności</h2>
          {hasAlert && (
            <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
              <AlertTriangle className="w-3 h-3" />
              Wysoka absencja
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period Toggle - Pill Slider */}
          <Tabs value={period} onValueChange={setPeriod} className="w-auto">
            <TabsList className="h-9 transition-all duration-300">
              <TabsTrigger value="weekly" className="text-xs px-3 transition-all duration-300">
                Tydzień
              </TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs px-3 transition-all duration-300">
                Miesiąc
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Group Filter */}
          <Select
            value={selectedGroup || 'all'}
            onValueChange={(v) => {
              setSelectedGroup(v === 'all' ? null : v);
              setSelectedChild(null);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Wszystkie grupy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie grupy</SelectItem>
              {groups.map(group => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Threshold Settings Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowThresholdSettings(!showThresholdSettings)}
            className={cn(showThresholdSettings && 'bg-slate-100')}
          >
            <Settings2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Threshold Settings Panel */}
      {showThresholdSettings && (
        <Card className="bg-slate-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 mb-2">
                  Próg alertu absencji: <span className="text-red-600">{localThreshold}%</span>
                </p>
                <Slider
                  value={[localThreshold]}
                  onValueChange={handleThresholdChange}
                  onValueCommit={handleThresholdCommit}
                  min={5}
                  max={50}
                  step={5}
                  className="w-full max-w-xs"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Alert pojawi się gdy absencja przekroczy {localThreshold}% dzieci
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trends Chart - Takes 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              Trendy nieobecności
            </CardTitle>
            <CardDescription>
              Dzienny procent nieobecności {period === 'weekly' ? 'w tym tygodniu' : 'w tym miesiącu'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {trendsLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendsData?.data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatChartDate}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <YAxis
                      domain={[0, 100]}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                            <p className="font-medium text-slate-900 mb-1">
                              {format(new Date(label), 'EEEE, d MMMM', { locale: pl })}
                            </p>
                            <p className="text-sm text-red-600">
                              Nieobecni: {payload[0]?.payload?.absent} ({payload[0]?.value}%)
                            </p>
                            <p className="text-xs text-slate-500">
                              Plan.: {payload[0]?.payload?.planned} | Nieplan.: {payload[0]?.payload?.unplanned}
                            </p>
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine
                      y={localThreshold}
                      stroke={COLORS.threshold}
                      strokeDasharray="5 5"
                      label={{
                        value: `Próg ${localThreshold}%`,
                        position: 'right',
                        fill: COLORS.threshold,
                        fontSize: 11
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="absentPercent"
                      name="Nieobecności"
                      stroke={COLORS.unplannedAbsence}
                      strokeWidth={2}
                      dot={{ r: 4, fill: COLORS.unplannedAbsence }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {trendsData?.totalChildren && (
              <p className="text-xs text-slate-500 mt-2 text-center">
                Na podstawie {trendsData.totalChildren} dzieci
              </p>
            )}
          </CardContent>
        </Card>

        {/* Distribution Pie Chart - Takes 1 column */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Rozkład obecności</CardTitle>
            <CardDescription>
              {period === 'weekly' ? 'Ten tydzień' : 'Ten miesiąc'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Child Filter for Pie Chart */}
            {selectedGroup && children.length > 0 && (
              <Select
                value={selectedChild || 'all'}
                onValueChange={(v) => setSelectedChild(v === 'all' ? null : v)}
              >
                <SelectTrigger className="w-full mb-4">
                  <SelectValue placeholder="Wszystkie dzieci" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie dzieci</SelectItem>
                  {children.map(child => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.first_name} {child.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {distributionLoading ? (
              <Skeleton className="h-48 w-full" />
            ) : pieData.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const item = payload[0];
                          const percent = distributionData?.total > 0
                            ? Math.round((item.value / distributionData.total) * 100)
                            : 0;
                          return (
                            <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-2">
                              <p className="text-sm font-medium">{item.name}</p>
                              <p className="text-sm text-slate-600">
                                {item.value} dni ({percent}%)
                              </p>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {pieData.map((item, index) => (
                    <div key={index} className="flex items-center gap-1.5 text-xs">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-slate-600">{item.name}</span>
                    </div>
                  ))}
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-green-700">
                      {distributionData?.total > 0
                        ? Math.round((distributionData.present / distributionData.total) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-green-600">Obecność</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-amber-700">
                      {distributionData?.plannedAbsence || 0}
                    </p>
                    <p className="text-xs text-amber-600">Plan.</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-red-700">
                      {distributionData?.unplannedAbsence || 0}
                    </p>
                    <p className="text-xs text-red-600">Nieplan.</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                Brak danych
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
